import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3, Plus, Search, Trash2 } from "lucide-react";
import {
  useFetchCareProgramsMutation,
  useCreateCareProgramMutation,
  useUpdateCareProgramMutation,
  useDeleteCareProgramMutation,
} from "../../../../features/criterion/criterionApiSlice";
import { selectUser } from "../../../../features/auth/authSlice";
import { useSelector } from "react-redux";

const CarePrograms = ({ isWelcomePage }) => {
  const user = useSelector(selectUser);
  const [fetchCarePrograms, { isLoading: isFetchingCarePrograms }] =
    useFetchCareProgramsMutation();
  const [createCareProgram, { isLoading: isCreatingCareProgram }] =
    useCreateCareProgramMutation();
  const [updateCareProgram, { isLoading: isUpdatingCareProgram }] =
    useUpdateCareProgramMutation();
  const [deleteCareProgram, { isLoading: isDeletingCareProgram }] =
    useDeleteCareProgramMutation();
  const [programs, setPrograms] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState({
    id: "",
    care_program_id_in_ehr: "",
    name: "",
    abbreviation: "",
  });
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const isSaving = isCreatingCareProgram || isUpdatingCareProgram;

  const extractPrograms = (res) =>
    res?.data ?? res?.data?.data ?? res?.result ?? res ?? [];

  const loadPrograms = useCallback(async () => {
    try {
      const res = await fetchCarePrograms().unwrap();
      setPrograms(extractPrograms(res));
      setPageError("");
    } catch (err) {
      setPageError(err?.data?.message || "Failed to load care programs.");
      setPrograms([]);
    }
  }, [fetchCarePrograms]);

  const filteredPrograms = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return programs;
    return programs.filter(
      (program) =>
        program.name.toLowerCase().includes(term) ||
        program.abbreviation.toLowerCase().includes(term)
    );
  }, [programs, search]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFormValues({
      id: "",
      care_program_id_in_ehr: "",
      name: "",
      abbreviation: "",
    });
    setError("");
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormValues({
      id: "",
      care_program_id_in_ehr: "",
      name: "",
      abbreviation: "",
    });
    setModalOpen(true);
  };

  const openEditModal = (program) => {
    console.log(program);
    setEditingId(program.id);
    setFormValues({
      id: program.id,
      care_program_id_in_ehr: program.care_program_id_in_ehr,
      name: program.name,
      abbreviation: program.abbreviation,
    });
    setModalOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const id = formValues.id;
    const careProgramIdInEhr = formValues.care_program_id_in_ehr.trim();
    const name = formValues.name.trim();
    const abbreviation = formValues.abbreviation.trim();

    if (!careProgramIdInEhr || !name || !abbreviation) {
      setError("All fields are required.");
      return;
    }

    const idExists =
      programs.some((program) => program.id === id) && editingId !== id;

    if (idExists) {
      setError("Program ID must be unique.");
      return;
    }

    const payload = {
      id,
      care_program_id_in_ehr: careProgramIdInEhr,
      name,
      abbreviation,
    };

    try {
      if (editingId) {
        await updateCareProgram(payload).unwrap();
      } else {
        await createCareProgram(payload).unwrap();
      }
      await loadPrograms();
      closeModal();
    } catch (err) {
      setError(
        err?.data?.message ||
          (editingId
            ? "Failed to update care program."
            : "Failed to create care program.")
      );
    }
  };

  const handleDelete = (program) => {
    setPendingDelete(program);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteCareProgram({ id: pendingDelete.id }).unwrap();
      await loadPrograms();
      setPendingDelete(null);
    } catch (err) {
      setPageError(err?.data?.message || "Failed to delete care program.");
    }
  };

  const cancelDelete = () => {
    setPendingDelete(null);
  };

  return (
    <div
      className={`relative flex flex-col w-full ${
        isWelcomePage ? "":"overflow-hidden"}`}
    >
      <div className="flex flex-col gap-3 px-4 py-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-3 items-center">
          <p className="m-0 text-2xl font-semibold text-gray-900">
            Care Program Configuration
          </p>
          <p className="m-0 text-sm text-gray-500">
            {filteredPrograms.length} Programs
          </p>
        </div>
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="relative flex-1 min-w-[240px] sm:max-w-sm">
            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search program..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pr-3 pl-10 w-full h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {user?.permissions?.includes('care_programs.create_care_programs') && (
            <button
              onClick={openAddModal}
              disabled={isFetchingCarePrograms}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus className="mr-2 w-4 h-4" />
              Add Program
            </button>
          )}
        </div>
      </div>
      {pageError && (
        <p className="px-4 pt-3 text-sm text-red-600">{pageError}</p>
      )}

      {user?.permissions?.includes('care_programs.view_care_programs') && (
        <div className="flex-1 px-4 pt-5 pb-6">
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-600">
              <tr>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Program ID
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Full Program Name
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Abbreviation
                </th>
                {(user?.permissions?.includes('care_programs.edit_care_programs') || user?.permissions?.includes('care_programs.delete_care_programs')) && (
                  <th className="px-6 py-3 text-sm font-medium text-right text-white">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isFetchingCarePrograms ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-sm text-center text-gray-500"
                  >
                    Loading programs...
                  </td>
                </tr>
              ) : filteredPrograms.length === 0 ? (
                <tr>
                  <td
                    colSpan={(user?.permissions?.includes('care_programs.edit_care_programs') || user?.permissions?.includes('care_programs.delete_care_programs')) ? 4 : 3}
                    className="px-6 py-8 text-sm text-center text-gray-500"
                  >
                    No programs found.
                  </td>
                </tr>
              ) : (
                filteredPrograms.map((program) => (
                  <tr
                    key={program.care_program_id_in_ehr}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {program.care_program_id_in_ehr}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {program.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {program.abbreviation}
                    </td>
                    {(user?.permissions?.includes('care_programs.edit_care_programs') || user?.permissions?.includes('care_programs.delete_care_programs')) && (
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <div className="flex gap-2 justify-end items-center">
                          {user?.permissions?.includes('care_programs.edit_care_programs') && (
                            <button
                              onClick={() => openEditModal(program)}
                              className="p-2 text-blue-600 rounded-md hover:bg-blue-50"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          )}
                          {user?.permissions?.includes('care_programs.delete_care_programs') && (
                            <button
                              onClick={() => handleDelete(program)}
                              className="p-2 text-red-600 rounded-md hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {modalOpen && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId ? "Edit Care Program" : "Add New Care Program"}
              </h3>
              <button
                onClick={closeModal}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <form className="flex flex-col gap-4" onSubmit={handleSave}>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Program ID
                </label>
                <input
                  type="text"
                  value={formValues.care_program_id_in_ehr}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      care_program_id_in_ehr: e.target.value,
                    }))
                  }
                  placeholder="e.g. CP-001"
                  className="px-3 h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="m-0 text-xs text-gray-500">
                  Enter a unique identifier for this program.
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Full Program Name
                </label>
                <input
                  type="text"
                  value={formValues.name}
                  onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. Diabetes Management"
                  className="px-3 h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="m-0 text-xs text-gray-500">
                  Enter complete program name.
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Care Program Abbreviation
                </label>
                <input
                  type="text"
                  value={formValues.abbreviation}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      abbreviation: e.target.value,
                    }))
                  }
                  placeholder="e.g. DM"
                  className="px-3 h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="m-0 text-xs text-gray-500">
                  Enter a short abbreviation (typically 2-4 letters).
                </p>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-3 justify-end items-center">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || (editingId ? !user?.permissions?.includes('care_programs.edit_care_programs') : !user?.permissions?.includes('care_programs.create_care_programs'))}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Care Program
              </h3>
              <button
                onClick={cancelDelete}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <p className="mb-6 text-sm text-gray-700">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{pendingDelete.name}</span> (
              {pendingDelete.id})? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end items-center">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeletingCareProgram}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeletingCareProgram ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarePrograms;
